// cache bust: list card update
import React, { Suspense } from 'react';
import { Loader2, AlertCircle, HelpCircle, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { GenUIComponentProps, GenUIAction } from '@/types/genui';

const ERROR_TRANSLATIONS: Array<[RegExp, string]> = [
  [/requires explicit user confirmation/i, '__CONFIRMATION__'],
  [/not found|no .* found|does not exist|404/i, 'That item was not found.'],
  [/not authorized|unauthorized|403|forbidden|don't have permission|access denied/i, "You don't have permission to do that."],
  [/already exists|duplicate entry|conflict|409/i, 'That already exists.'],
  [/invalid.*input|validation.*fail|bad request|400/i, 'The request had invalid parameters.'],
  [/service method threw.*ENOTFOUND|ECONNREFUSED|network/i, 'Could not reach the service. Please try again.'],
  [/rate limit|too many requests|429/i, 'Too many requests. Please wait a moment and try again.'],
  [/timeout|ETIMEDOUT/i, 'The request timed out. Please try again.'],
  [/service method threw:\s*/i, ''],
  [/tool execution failed:\s*/i, ''],
];

// If the backend error already includes an actionable hint (a list of valid
// options, a suggestion, or a "try X" pointer), surface it verbatim instead of
// flattening to a generic chip. Lets server-side errors like
// `Template "X" does not match any approved template ... Available approved templates (showing 15 of 50): A, B, C.` reach the
// LLM + the user — far more useful than `That item was not found.`
//
// The `available` branch tolerates inserted words between "available" and the
// colon (e.g. "Available approved templates (showing 15 of 50):") so newer
// error wordings keep flowing through. The other branches are strict "<phrase>:".
const ACTIONABLE_HINT_RE = /\b(?:available[^:]{0,80}|did you mean|try one of|valid options):/i;

function translateError(raw: string): string {
  if (ACTIONABLE_HINT_RE.test(raw)) return raw;

  for (const [pattern, replacement] of ERROR_TRANSLATIONS) {
    if (pattern.test(raw)) {
      if (replacement === '__CONFIRMATION__') return raw;
      if (replacement === '') {
        const cleaned = raw.replace(pattern, '').trim();
        return cleaned || raw;
      }
      return replacement;
    }
  }
  return raw;
}

function formatToolError(error: unknown): string {
  if (!error) return 'Unknown error';

  let raw: string;
  if (typeof error === 'string') {
    raw = error;
  } else if (error instanceof Error) {
    raw = error.message || 'Unknown error';
  } else if (typeof error === 'object') {
    const maybeError = error as Record<string, unknown>;
    if (typeof maybeError.message === 'string' && maybeError.message) raw = maybeError.message;
    else if (typeof maybeError.error === 'string' && maybeError.error) raw = maybeError.error;
    else if (typeof maybeError.details === 'string' && maybeError.details) raw = maybeError.details;
    else { try { raw = JSON.stringify(error); } catch { raw = 'Unknown error'; } }
  } else {
    raw = String(error);
  }

  return translateError(raw);
}

// ─── Registry (lazy-loaded) ─────────────────────────────────────────────────

const registry: Record<string, React.LazyExoticComponent<React.FC<GenUIComponentProps>>> = {
  // ─── Generic ──────────────────────────────────────────────────────────────────
  GenericTableCard: React.lazy(() => import('./registry/cards/generic/GenericTableCard')),
  GenericChartCard: React.lazy(() => import('./registry/cards/generic/GenericChartCard')),
  SuccessCard: React.lazy(() => import('./registry/cards/generic/SuccessCard')),
  ErrorCard: React.lazy(() => import('./registry/cards/generic/ErrorCard')),
  NavigationCard: React.lazy(() => import('./registry/cards/generic/NavigationCard')),
  StatusConfirmation: React.lazy(() => import('./registry/cards/generic/StatusConfirmationCard')),
  NoteConfirmation: React.lazy(() => import('./registry/cards/generic/NoteConfirmationCard')),
  AgentRedirectCard: React.lazy(() => import('./registry/cards/generic/AgentRedirectCard')),
  AgentSuggestionChip: React.lazy(() => import('./registry/cards/generic/AgentSuggestionChip')),
  GrowthChartCard: React.lazy(() => import('./registry/cards/generic/GrowthChartCard')),
  StatsCard: React.lazy(() => import('./registry/cards/generic/StatsCard')),

  // ─── Dashboard ────────────────────────────────────────────────────────────────
  DashboardStatsCard: React.lazy(() => import('./registry/cards/dashboard/DashboardStatsCard')),
  DashboardSummaryCard: React.lazy(() => import('./registry/cards/analytics/DashboardSummaryCard')),

  // ─── Inbox ────────────────────────────────────────────────────────────────────
  InboxSummaryCard: React.lazy(() => import('./registry/cards/inbox/InboxSummaryCard')),
  ConversationListCard: React.lazy(() => import('./registry/cards/inbox/ConversationListCard')),
  ConversationDetailCard: React.lazy(() => import('./registry/cards/inbox/ConversationDetailCard')),
  AgentAssignmentCard: React.lazy(() => import('./registry/cards/inbox/AgentAssignmentCard')),
  UnreadCountCard: React.lazy(() => import('./registry/cards/inbox/UnreadCountCard')),
  MessageListCard: React.lazy(() => import('./registry/cards/inbox/MessageListCard')),

  // ─── Contacts ─────────────────────────────────────────────────────────────────
  ContactListCard: React.lazy(() => import('./registry/cards/contacts/ContactListCard')),
  ContactDetailCard: React.lazy(() => import('./registry/cards/contacts/ContactDetailCard')),
  ImportStatusCard: React.lazy(() => import('./registry/cards/contacts/ImportStatusCard')),
  TagListCard: React.lazy(() => import('./registry/cards/contacts/TagListCard')),
  TagFormCard: React.lazy(() => import('./registry/cards/contacts/TagFormCard')),
  TagAssignedCard: React.lazy(() => import('./registry/cards/contacts/TagAssignedCard')),
  contacts_show_tag_form: React.lazy(() => import('./registry/cards/contacts/TagFormCard')),
  contacts_assign_tag: React.lazy(() => import('./registry/cards/contacts/TagAssignedCard')),

  // ─── Campaigns ────────────────────────────────────────────────────────────────
  CampaignListCard: React.lazy(() => import('./registry/cards/campaigns/CampaignListCard')),
  CampaignDetailCard: React.lazy(() => import('./registry/cards/campaigns/CampaignDetailCard')),
  CampaignSummaryCard: React.lazy(() => import('./registry/cards/campaigns/CampaignSummaryCard')),
  CampaignFormCard: React.lazy(() => import('./registry/cards/campaigns/CampaignFormCard')),
  CampaignAnalyticsCard: React.lazy(() => import('./registry/cards/campaigns/CampaignAnalyticsCard')),
  CampaignStatusCard: React.lazy(() => import('./registry/cards/campaigns/CampaignStatusCard')),
  CampaignPerformanceCard: React.lazy(() => import('./registry/cards/analytics/CampaignPerformanceCard')),
  RecipientListCard: React.lazy(() => import('./registry/cards/campaigns/RecipientListCard')),
  CampaignCard: React.lazy(() => import('./registry/cards/campaigns/CampaignCard')),
  CampaignRunCard: React.lazy(() => import('./registry/cards/campaigns/CampaignRunCard')),

  // ─── Analytics ────────────────────────────────────────────────────────────────
  AnalyticsSummaryCard: React.lazy(() => import('./registry/cards/analytics/AnalyticsSummaryCard')),
  MessageReportCard: React.lazy(() => import('./registry/cards/analytics/MessageReportCard')),
  SalesConversionCard: React.lazy(() => import('./registry/cards/analytics/SalesConversionCard')),
  AdsResultsCard: React.lazy(() => import('./registry/cards/analytics/AdsResultsCard')),
  AnalyticsOverviewCard: React.lazy(() => import('./registry/cards/analytics/AnalyticsOverviewCard')),
  ConversationStatsCard: React.lazy(() => import('./registry/cards/analytics/ConversationStatsCard')),
  AgentStatsCard: React.lazy(() => import('./registry/cards/analytics/AgentStatsCard')),

  // ─── Commerce ─────────────────────────────────────────────────────────────────
  ProductListCard: React.lazy(() => import('./registry/cards/commerce/ProductListCard')),
  ProductDetailCard: React.lazy(() => import('./registry/cards/commerce/ProductDetailCard')),
  OrderListCard: React.lazy(() => import('./registry/cards/commerce/OrderListCard')),
  OrderDetailCard: React.lazy(() => import('./registry/cards/commerce/OrderDetailCard')),
  CommerceStatsCard: React.lazy(() => import('./registry/cards/commerce/CommerceStatsCard')),
  CommerceDashboardCard: React.lazy(() => import('./registry/cards/commerce/CommerceDashboardCard')),
  PaymentListCard: React.lazy(() => import('./registry/cards/commerce/PaymentListCard')),
  AbandonedCartListCard: React.lazy(() => import('./registry/cards/commerce/AbandonedCartListCard')),
  AbandonedCartCard: React.lazy(() => import('./registry/cards/commerce/AbandonedCartListCard')),
  InvoiceListCard: React.lazy(() => import('./registry/cards/commerce/InvoiceListCard')),
  InvoiceCard: React.lazy(() => import('./registry/cards/commerce/InvoiceCard')),
  InvoiceDetailCard: React.lazy(() => import('./registry/cards/commerce/InvoiceCard')),

  // ─── Automation ───────────────────────────────────────────────────────────────
  FlowListCard: React.lazy(() => import('./registry/cards/automation/FlowListCard')),
  FlowDetailCard: React.lazy(() => import('./registry/cards/automation/FlowDetailCard')),
  FlowStatusCard: React.lazy(() => import('./registry/cards/automation/FlowStatusCard')),
  ExecutionListCard: React.lazy(() => import('./registry/cards/automation/ExecutionListCard')),
  ExecutionDetailCard: React.lazy(() => import('./registry/cards/automation/ExecutionDetailCard')),

  // ─── CRM ──────────────────────────────────────────────────────────────────────
  LeadListCard: React.lazy(() => import('./registry/cards/crm/LeadListCard')),
  LeadDetailCard: React.lazy(() => import('./registry/cards/crm/LeadDetailCard')),
  SegmentListCard: React.lazy(() => import('./registry/cards/crm/SegmentListCard')),
  BoardListCard: React.lazy(() => import('./registry/cards/crm/BoardListCard')),
  KanbanBoardCard: React.lazy(() => import('./registry/cards/crm/KanbanBoardCard')),
  BoardDetailCard: React.lazy(() => import('./registry/cards/crm/BoardDetailCard')),

  // ─── Scheduling ───────────────────────────────────────────────────────────────
  ScheduleOverviewCard: React.lazy(() => import('./registry/cards/scheduling/ScheduleOverviewCard')),
  SchedulingOverviewCard: React.lazy(() => import('./registry/cards/scheduling/SchedulingOverviewCard')),
  EventTypeListCard: React.lazy(() => import('./registry/cards/scheduling/EventTypeListCard')),
  EventListCard: React.lazy(() => import('./registry/cards/scheduling/EventListCard')),
  TimeSlotCard: React.lazy(() => import('./registry/cards/scheduling/TimeSlotCard')),
  MeetingLinkListCard: React.lazy(() => import('./registry/cards/scheduling/MeetingLinkListCard')),
  BookingDetailCard: React.lazy(() => import('./registry/cards/scheduling/BookingDetailCard')),
  EventTypeCard: React.lazy(() => import('./registry/cards/scheduling/EventTypeCard')),

  // ─── Social ───────────────────────────────────────────────────────────────────
  SocialPostsCard: React.lazy(() => import('./registry/cards/social/SocialPostsCard')),
  SocialAccountsCard: React.lazy(() => import('./registry/cards/social/SocialAccountsCard')),
  SocialInboxCard: React.lazy(() => import('./registry/cards/social/SocialInboxCard')),
  AiImageCard: React.lazy(() => import('./registry/cards/social/AiImageCard')),
  InstagramInsightsCard: React.lazy(() => import('./registry/cards/social/InstagramInsightsCard')),
  FacebookInsightsCard: React.lazy(() => import('./registry/cards/social/FacebookInsightsCard')),
  LinkedInInsightsCard: React.lazy(() => import('./registry/cards/social/LinkedInInsightsCard')),
  InstagramFlowListCard: React.lazy(() => import('./registry/cards/social/InstagramFlowListCard')),
  InstagramFlowDetailCard: React.lazy(() => import('./registry/cards/social/InstagramFlowDetailCard')),
  InstagramPostListCard: React.lazy(() => import('./registry/cards/social/InstagramPostListCard')),

  // ─── Ads ──────────────────────────────────────────────────────────────────────
  AdsSummaryCard: React.lazy(() => import('./registry/cards/ads/AdsSummaryCard')),
  AdSummaryCard: React.lazy(() => import('./registry/cards/ads/AdSummaryCard')),
  AdCampaignListCard: React.lazy(() => import('./registry/cards/ads/AdCampaignListCard')),
  AdCampaignDetailCard: React.lazy(() => import('./registry/cards/ads/AdCampaignDetailCard')),
  AdAnalyticsCard: React.lazy(() => import('./registry/cards/ads/AdAnalyticsCard')),

  // ─── Integrations ─────────────────────────────────────────────────────────────
  IntegrationStatusCard: React.lazy(() => import('./registry/cards/integrations/IntegrationStatusCard')),
  ConnectIntegrationCard: React.lazy(() => import('./registry/cards/integrations/ConnectIntegrationCard')),

  // ─── Settings ─────────────────────────────────────────────────────────────────
  TeamMembersCard: React.lazy(() => import('./registry/cards/settings/TeamMembersCard')),
  TeamMemberListCard: React.lazy(() => import('./registry/cards/settings/TeamMembersCard')),
  ProfileCard: React.lazy(() => import('./registry/cards/settings/ProfileCard')),
  BusinessAccountCard: React.lazy(() => import('./registry/cards/settings/BusinessAccountCard')),
  BusinessAccountListCard: React.lazy(() => import('./registry/cards/settings/BusinessAccountListCard')),
  TeamListCard: React.lazy(() => import('./registry/cards/settings/TeamListCard')),
  TeamDetailCard: React.lazy(() => import('./registry/cards/settings/TeamDetailCard')),
  TeamStatsCard: React.lazy(() => import('./registry/cards/settings/TeamStatsCard')),

  // ─── WhatsApp ─────────────────────────────────────────────────────────────────
  WhatsAppFlowsCard: React.lazy(() => import('./registry/cards/whatsapp/WhatsAppFlowsCard')),
  WhatsAppFlowListCard: React.lazy(() => import('./registry/cards/whatsapp/WhatsAppFlowsCard')),
  WhatsAppFlowDetailCard: React.lazy(() => import('./registry/cards/whatsapp/WhatsAppFlowDetailCard')),
  WhatsAppFlowFormCard: React.lazy(() => import('./registry/cards/whatsapp/WhatsAppFlowFormCard')),
  TemplateListCard: React.lazy(() => import('./registry/cards/whatsapp/TemplateListCard')),
  TemplateDetailCard: React.lazy(() => import('./registry/cards/whatsapp/TemplateDetailCard')),
  TemplateFormCard: React.lazy(() => import('./registry/cards/whatsapp/TemplateFormCard')),
  TemplateAnalyticsCard: React.lazy(() => import('./registry/cards/whatsapp/TemplateAnalyticsCard')),
  ReminderListCard: React.lazy(() => import('./registry/cards/whatsapp/ReminderListCard')),
  ReminderDetailCard: React.lazy(() => import('./registry/cards/whatsapp/ReminderDetailCard')),
  CallLogListCard: React.lazy(() => import('./registry/cards/whatsapp/CallLogListCard')),
  AgentDetailCard: React.lazy(() => import('./registry/cards/whatsapp/AgentDetailCard')),
  AiAgentListCard: React.lazy(() => import('./registry/cards/whatsapp/AiAgentListCard')),
  QuickReplyListCard: React.lazy(() => import('./registry/cards/whatsapp/QuickReplyListCard')),
  ScheduledMessageCard: React.lazy(() => import('./registry/cards/whatsapp/ScheduledMessageCard')),
  PaymentLinkCard: React.lazy(() => import('./registry/cards/whatsapp/PaymentLinkCard')),
  AiAgentCard: React.lazy(() => import('./registry/cards/ai-agent/AiAgentCard')),
  AiAgentDeleteCard: React.lazy(() => import('./registry/cards/ai-agent/AiAgentDeleteCard')),
  VoiceAgentFormCard: React.lazy(() => import('./registry/cards/ai-agent/VoiceAgentFormCard')),
  VoiceAgentListCard: React.lazy(() => import('./registry/cards/ai-agent/VoiceAgentListCard')),
  VoiceAgentDeleteCard: React.lazy(() => import('./registry/cards/ai-agent/VoiceAgentDeleteCard')),
  WbAiAgentCard: React.lazy(() => import('./registry/cards/whatsapp/WbAiAgentCard')),
  WbAiAgentDeleteCard: React.lazy(() => import('./registry/cards/whatsapp/WbAiAgentDeleteCard')),
  LiveChatScriptCard: React.lazy(() => import('./registry/cards/live-chat/LiveChatScriptCard')),
  LiveChatListCard: React.lazy(() => import('./registry/cards/live-chat/LiveChatListCard')),
  LiveChatDeleteCard: React.lazy(() => import('./registry/cards/live-chat/LiveChatDeleteCard')),

  // ─── Billing ──────────────────────────────────────────────────────────────────
  BillingSummaryCard: React.lazy(() => import('./registry/cards/billing/BillingSummaryCard')),
  SubscriptionCard: React.lazy(() => import('./registry/cards/billing/SubscriptionCard')),
  SubscriptionListCard: React.lazy(() => import('./registry/cards/billing/SubscriptionCard')),
  PricingTableCard: React.lazy(() => import('./registry/cards/billing/PricingTableCard')),
  UsageCard: React.lazy(() => import('./registry/cards/billing/UsageCard')),
  CreditBalanceCard: React.lazy(() => import('./registry/cards/billing/CreditBalanceCard')),

  // ─── Data Store ───────────────────────────────────────────────────────────────
  DataStoreRecordsCard: React.lazy(() => import('./registry/cards/data-store/DataStoreRecordsCard')),
  DataStoreListCard: React.lazy(() => import('./registry/cards/data-store/DataStoreListCard')),
  DataRecordListCard: React.lazy(() => import('./registry/cards/data-store/DataRecordListCard')),
  DataRecordDetailCard: React.lazy(() => import('./registry/cards/data-store/DataRecordDetailCard')),
  FormSubmissionListCard: React.lazy(() => import('./registry/cards/data-store/FormSubmissionListCard')),
  DataStoreFieldsCard: React.lazy(() => import('./registry/cards/data-store/DataStoreFieldsCard')),

  // ─── Catalog ──────────────────────────────────────────────────────────────────
  CatalogListCard: React.lazy(() => import('./registry/cards/catalog/CatalogListCard')),
  CatalogDetailCard: React.lazy(() => import('./registry/cards/catalog/CatalogDetailCard')),
  CatalogSettingsCard: React.lazy(() => import('./registry/cards/catalog/CatalogSettingsCard')),
  CollectionListCard: React.lazy(() => import('./registry/cards/catalog/CollectionListCard')),

  // ─── Admin (super-admin only) ─────────────────────────────────────────────────
  AdminDashboardCard: React.lazy(() => import('./registry/cards/admin/AdminDashboardCard')),
  SystemHealthCard: React.lazy(() => import('./registry/cards/admin/SystemHealthCard')),
  RevenueCard: React.lazy(() => import('./registry/cards/admin/RevenueCard')),
  RevenueTrendCard: React.lazy(() => import('./registry/cards/admin/RevenueTrendCard')),
  ClientListCard: React.lazy(() => import('./registry/cards/admin/ClientListCard')),
  ClientDetailCard: React.lazy(() => import('./registry/cards/admin/ClientDetailCard')),
  UserListCard: React.lazy(() => import('./registry/cards/admin/UserListCard')),
  AuditLogListCard: React.lazy(() => import('./registry/cards/admin/AuditLogListCard')),
  CampaignActivityCard: React.lazy(() => import('./registry/cards/admin/CampaignActivityCard')),
  ActivityFeedCard: React.lazy(() => import('./registry/cards/admin/ActivityFeedCard')),

  // ─── Help ─────────────────────────────────────────────────────────────────────
  HelpArticleCard: React.lazy(() => import('./registry/cards/help/HelpArticleCard')),
  HelpArticleListCard: React.lazy(() => import('./registry/cards/help/HelpArticleListCard')),
  TicketListCard: React.lazy(() => import('./registry/cards/help/TicketListCard')),
  TicketDetailCard: React.lazy(() => import('./registry/cards/help/TicketDetailCard')),

  // ─── Notifications ────────────────────────────────────────────────────────────
  NotificationListCard: React.lazy(() => import('./registry/cards/notifications/NotificationListCard')),

  // ─── Media ────────────────────────────────────────────────────────────────────
  MediaLibraryCard: React.lazy(() => import('./registry/cards/media/MediaLibraryCard')),

  // ─── Petpooja ─────────────────────────────────────────────────────────────────
  PetpoojaSummaryCard: React.lazy(() => import('./registry/cards/petpooja/PetpoojaSummaryCard')),
  PetpoojaRestaurantListCard: React.lazy(() => import('./registry/cards/petpooja/PetpoojaRestaurantListCard')),
  PetpoojaCategoryListCard: React.lazy(() => import('./registry/cards/petpooja/PetpoojaCategoryListCard')),
  PetpoojaItemListCard: React.lazy(() => import('./registry/cards/petpooja/PetpoojaItemListCard')),
  PetpoojaOrderListCard: React.lazy(() => import('./registry/cards/petpooja/PetpoojaOrderListCard')),

  // ─── Tool-name aliases (backend toolName → component) ─────────────────────────
  list_automation_flows: React.lazy(() => import('./registry/cards/automation/FlowListCard')),
  get_campaign_analytics: React.lazy(() => import('./registry/cards/campaigns/CampaignSummaryCard')),
  list_campaigns: React.lazy(() => import('./registry/cards/campaigns/CampaignListCard')),
  search_contacts: React.lazy(() => import('./registry/cards/contacts/ContactListCard')),
  get_conversations: React.lazy(() => import('./registry/cards/inbox/ConversationListCard')),
  get_dashboard_stats: React.lazy(() => import('./registry/cards/dashboard/DashboardStatsCard')),
  get_analytics: React.lazy(() => import('./registry/cards/analytics/AnalyticsSummaryCard')),
  list_orders: React.lazy(() => import('./registry/cards/commerce/OrderListCard')),
  get_subscription_info: React.lazy(() => import('./registry/cards/billing/BillingSummaryCard')),
  get_social_posts: React.lazy(() => import('./registry/cards/social/SocialPostsCard')),
  get_team_members: React.lazy(() => import('./registry/cards/settings/TeamMembersCard')),
  get_profile: React.lazy(() => import('./registry/cards/settings/ProfileCard')),
  generate_ai_image: React.lazy(() => import('./registry/cards/social/AiImageCard')),
  livechat_create: React.lazy(() => import('./registry/cards/live-chat/LiveChatScriptCard')),
  livechat_list: React.lazy(() => import('./registry/cards/live-chat/LiveChatListCard')),
  livechat_delete: React.lazy(() => import('./registry/cards/live-chat/LiveChatDeleteCard')),
  templates_create: React.lazy(() => import('./registry/cards/whatsapp/TemplateFormCard')),
  whatsapp_create_form: React.lazy(() => import('./registry/cards/whatsapp/WhatsAppFlowFormCard')),
  campaigns_create: React.lazy(() => import('./registry/cards/campaigns/CampaignDetailCard')),
  templates_list: React.lazy(() => import('./registry/cards/whatsapp/TemplateListCard')),
  templates_get: React.lazy(() => import('./registry/cards/whatsapp/TemplateDetailCard')),
  templates_search: React.lazy(() => import('./registry/cards/whatsapp/TemplateListCard')),

  // New inbox tools aliases
  inbox_list_conversations: React.lazy(() => import('./registry/cards/inbox/ConversationListCard')),
  inbox_get_conversation: React.lazy(() => import('./registry/cards/inbox/ConversationDetailCard')),
  inbox_send_message: React.lazy(() => import('./registry/cards/generic/SuccessCard')),
  inbox_assign_agent: React.lazy(() => import('./registry/cards/inbox/AgentAssignmentCard')),
  inbox_assign_to_member: React.lazy(() => import('./registry/cards/inbox/AgentAssignmentCard')),
  inbox_mark_read: React.lazy(() => import('./registry/cards/generic/StatusConfirmationCard')),
  inbox_get_unread_count: React.lazy(() => import('./registry/cards/inbox/UnreadCountCard')),
  inbox_search_conversations: React.lazy(() => import('./registry/cards/inbox/ConversationListCard')),
  inbox_get_summary: React.lazy(() => import('./registry/cards/inbox/InboxSummaryCard')),
  inbox_mark_all_read: React.lazy(() => import('./registry/cards/generic/SuccessCard')),
  inbox_get_messages: React.lazy(() => import('./registry/cards/inbox/MessageListCard')),
  inbox_create_conversation: React.lazy(() => import('./registry/cards/inbox/ConversationDetailCard')),
  inbox_step_in: React.lazy(() => import('./registry/cards/generic/SuccessCard')),
  inbox_star_message: React.lazy(() => import('./registry/cards/generic/SuccessCard')),
  inbox_unstar_message: React.lazy(() => import('./registry/cards/generic/SuccessCard')),
  inbox_get_starred_messages: React.lazy(() => import('./registry/cards/inbox/MessageListCard')),
  inbox_forward_message: React.lazy(() => import('./registry/cards/generic/SuccessCard')),
  inbox_get_starred_conversations: React.lazy(() => import('./registry/cards/inbox/ConversationListCard')),
  inbox_add_reaction: React.lazy(() => import('./registry/cards/generic/SuccessCard')),
  inbox_remove_reaction: React.lazy(() => import('./registry/cards/generic/SuccessCard')),
  inbox_close_conversation: React.lazy(() => import('./registry/cards/generic/SuccessCard')),
  inbox_reopen_conversation: React.lazy(() => import('./registry/cards/generic/SuccessCard')),

  // New contacts tools aliases
  contacts_list: React.lazy(() => import('./registry/cards/contacts/ContactListCard')),
  contacts_get: React.lazy(() => import('./registry/cards/contacts/ContactDetailCard')),
  contacts_create: React.lazy(() => import('./registry/cards/contacts/ContactDetailCard')),
  contacts_update: React.lazy(() => import('./registry/cards/contacts/ContactDetailCard')),
  contacts_search: React.lazy(() => import('./registry/cards/contacts/ContactListCard')),
  contacts_add_tags: React.lazy(() => import('./registry/cards/generic/SuccessCard')),
  contacts_delete: React.lazy(() => import('./registry/cards/generic/SuccessCard')),
  contacts_list_tags: React.lazy(() => import('./registry/cards/contacts/TagListCard')),
  contacts_delete_tag: React.lazy(() => import('./registry/cards/generic/SuccessCard')),
  contacts_block: React.lazy(() => import('./registry/cards/generic/SuccessCard')),
  contacts_unblock: React.lazy(() => import('./registry/cards/generic/SuccessCard')),
  contacts_bulk_add_tags: React.lazy(() => import('./registry/cards/generic/SuccessCard')),
  contacts_pause_flows: React.lazy(() => import('./registry/cards/generic/SuccessCard')),
  contacts_remove_tags: React.lazy(() => import('./registry/cards/generic/SuccessCard')),
  contacts_update_tag: React.lazy(() => import('./registry/cards/generic/SuccessCard')),
  contacts_get_stats: React.lazy(() => import('./registry/cards/generic/StatsCard')),
  contacts_get_growth: React.lazy(() => import('./registry/cards/generic/GrowthChartCard')),
  contacts_bulk_import: React.lazy(() => import('./registry/cards/contacts/ImportStatusCard')),
  voice_agent_create: React.lazy(() => import('./registry/cards/ai-agent/VoiceAgentFormCard')),
  voice_agent_list: React.lazy(() => import('./registry/cards/ai-agent/VoiceAgentListCard')),
  voice_agent_delete: React.lazy(() => import('./registry/cards/ai-agent/VoiceAgentDeleteCard')),
};

// ─── Skeleton ────────────────────────────────────────────────────────────────

const ToolCallSkeleton: React.FC<{ toolName?: string }> = ({ toolName }) => (
  <Card className="border-gray-100 animate-pulse">
    <CardContent className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
        <Badge variant="secondary" className="text-xs">
          {(toolName || 'tool').replace(/_/g, ' ')}
        </Badge>
      </div>
      <div className="space-y-2">
        <div className="h-4 bg-gray-100 rounded w-3/4" />
        <div className="h-4 bg-gray-100 rounded w-1/2" />
        <div className="h-4 bg-gray-100 rounded w-2/3" />
      </div>
    </CardContent>
  </Card>
);

// ─── Error Fallback ──────────────────────────────────────────────────────────

const ToolCallError: React.FC<{ toolName?: string; error: unknown }> = ({ toolName, error }) => {
  const errorStr = formatToolError(error);
  const isConfirmation = /requires explicit user confirmation/i.test(errorStr);

  if (isConfirmation) {
    const label = (toolName || 'action').replace(/_/g, ' ');
    return (
      <Card className="border-violet-200 bg-gradient-to-r from-violet-50/80 to-indigo-50/60">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-100">
              <ShieldCheck className="w-4 h-4 text-violet-600" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-violet-800 capitalize">{label}</p>
              <p className="text-xs text-violet-600/80 mt-0.5">
                Review the details above and reply <span className="font-medium text-violet-700">"yes"</span> or <span className="font-medium text-violet-700">"go ahead"</span> to confirm.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-red-100 bg-red-50/50">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm font-medium">{(toolName || 'tool').replace(/_/g, ' ')}</span>
        </div>
        <p className="text-xs text-red-500 mt-1">{errorStr}</p>
      </CardContent>
    </Card>
  );
};

// ─── Unknown Tool Fallback ───────────────────────────────────────────────────

function summarizeData(data: unknown): { lines: string[]; count?: number } {
  if (!data || typeof data !== 'object') return { lines: [] };
  const d = data as Record<string, unknown>;

  // Count-bearing list results
  const arr = d.items || d.data || d.rows || d.results;
  if (Array.isArray(arr)) {
    const total = Number(d.total_count ?? d.total ?? d.count ?? arr.length);
    const names = arr.slice(0, 3).map((item: any) =>
      item?.name || item?.title || item?.label || item?.id || null
    ).filter(Boolean);
    return { lines: names as string[], count: total };
  }

  // Single object — surface a handful of readable fields
  const SKIP = new Set(['id', 'organization_id', 'created_at', 'updated_at', 'success', '__typename']);
  const lines: string[] = [];
  for (const [k, v] of Object.entries(d)) {
    if (SKIP.has(k) || v == null || v === '' || typeof v === 'object') continue;
    lines.push(`${k.replace(/_/g, ' ')}: ${String(v)}`);
    if (lines.length >= 5) break;
  }
  return { lines };
}

const UnknownToolFallback: React.FC<{ toolName?: string; data: unknown }> = ({ toolName, data }) => {
  const label = (toolName || 'tool').replace(/_/g, ' ');
  const d = data as any;
  const isSuccess = d?.success !== false;

  if (isSuccess) {
    const { lines, count } = summarizeData(data);
    return (
      <Card className="border-gray-100">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 mb-1">
            <HelpCircle className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs font-medium text-gray-600 capitalize">{label}</span>
            {count != null && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{count}</Badge>
            )}
          </div>
          {lines.length > 0 && (
            <ul className="mt-1 space-y-0.5 pl-5 list-disc text-xs text-gray-500">
              {lines.map((l, i) => <li key={i}>{l}</li>)}
              {count != null && count > lines.length && (
                <li className="text-gray-400">+{count - lines.length} more…</li>
              )}
            </ul>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-gray-100">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <HelpCircle className="w-4 h-4 text-gray-400" />
          <Badge variant="outline" className="text-xs capitalize">{label}</Badge>
        </div>
        <pre className="text-xs text-gray-600 bg-gray-50 rounded p-2 max-h-40 overflow-y-auto whitespace-pre-wrap">
          {JSON.stringify(data, null, 2)?.slice(0, 500)}
        </pre>
      </CardContent>
    </Card>
  );
};

// ─── Error Boundary ──────────────────────────────────────────────────────────

class GenUIErrorBoundary extends React.Component<
  { toolName?: string; children: React.ReactNode },
  { hasError: boolean; error: string }
> {
  constructor(props: { toolName?: string; children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: '' };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error: error.message };
  }

  render() {
    if (this.state.hasError) {
      return <ToolCallError toolName={this.props.toolName} error={this.state.error} />;
    }
    return this.props.children;
  }
}

// ─── Main Renderer ───────────────────────────────────────────────────────────

interface GenUIComponentRendererProps {
  toolName: string;
  toolCallId: string;
  data: unknown;
  status: 'pending' | 'completed' | 'error';
  error?: string;
  componentHint?: string;
  onAction: (action: GenUIAction) => void;
}

export const GenUIComponentRenderer: React.FC<GenUIComponentRendererProps> = ({
  toolName,
  toolCallId,
  data,
  status,
  error,
  componentHint,
  onAction,
}) => {
  if (status === 'pending') {
    return <ToolCallSkeleton toolName={toolName} />;
  }

  if (status === 'error') {
    return <ToolCallError toolName={toolName} error={error || 'Unknown error'} />;
  }

  // A tool that EXPLICITLY returns { success: false } failed. Render a clean ErrorCard
  // with its message — NEVER the tool's own success card, which would show a confusing
  // secondary error (e.g. WhatsAppFlowFormCard's "Missing flow definition data" when a
  // form create failed because WhatsApp isn't connected). This is the handler failure
  // convention across GenUI, so it applies to every tool, not just one.
  if (data && typeof data === 'object' && (data as Record<string, unknown>).success === false) {
    const failMsg =
      (data as Record<string, unknown>).error ||
      (data as Record<string, unknown>).message ||
      'This action could not be completed.';
    const ErrorComp = registry['ErrorCard'];
    return (
      <GenUIErrorBoundary toolName={toolName}>
        <Suspense fallback={<ToolCallSkeleton toolName={toolName} />}>
          <ErrorComp data={{ message: failMsg }} toolCallId={toolCallId} onAction={onAction} />
        </Suspense>
      </GenUIErrorBoundary>
    );
  }

  // Special case: get_campaign_analytics with all_campaigns=true → list view
  const resolvedToolName =
    toolName === 'get_campaign_analytics' && (data as any)?.all_campaigns
      ? 'list_campaigns'
      : toolName;

  // Look up component by componentHint first, then resolved toolName
  const Component = (componentHint && registry[componentHint]) || registry[resolvedToolName];

  if (!Component) {
    return <UnknownToolFallback toolName={toolName} data={data} />;
  }

  return (
    <GenUIErrorBoundary toolName={toolName}>
      <Suspense fallback={<ToolCallSkeleton toolName={toolName} />}>
        <Component data={data} toolCallId={toolCallId} onAction={onAction} />
      </Suspense>
    </GenUIErrorBoundary>
  );
};
